import api from "./client";
import type { LineItem } from "@/types/invoice";
import type {
  RecurringCadence,
  RecurringInvoice,
  RecurringStatus,
  RunDueResult,
} from "@/types/recurringInvoice";

export interface CreateRecurringInvoiceBody {
  employee_id: string;
  title: string;
  line_items: LineItem[];
  cadence: RecurringCadence;
  start_date: string;
  end_date?: string;
  max_occurrences?: number;
  currency?: string;
  tax_rate?: number;
  due_days_after_issue?: number;
  terms?: string;
  notes?: string;
  auto_send?: boolean;
  send_to_email?: string;
}

export interface UpdateRecurringInvoiceBody {
  title?: string;
  line_items_json?: LineItem[];
  cadence?: RecurringCadence;
  end_date?: string;
  max_occurrences?: number;
  currency?: string;
  tax_rate?: number;
  due_days_after_issue?: number;
  terms?: string;
  notes?: string;
  auto_send?: boolean;
  send_to_email?: string;
}

export async function listRecurringInvoices(
  status?: RecurringStatus,
  employeeId?: string,
): Promise<{ items: RecurringInvoice[] }> {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  if (employeeId) params.employee_id = employeeId;
  const { data } = await api.get("/invoices/recurring", { params });
  return data;
}

export async function getRecurringInvoice(id: string): Promise<RecurringInvoice> {
  const { data } = await api.get(`/invoices/recurring/${id}`);
  return data;
}

export async function createRecurringInvoice(
  body: CreateRecurringInvoiceBody,
): Promise<RecurringInvoice> {
  const { data } = await api.post("/invoices/recurring", body);
  return data;
}

export async function updateRecurringInvoice(
  id: string,
  body: UpdateRecurringInvoiceBody,
): Promise<RecurringInvoice> {
  const { data } = await api.put(`/invoices/recurring/${id}`, body);
  return data;
}

export async function pauseRecurringInvoice(id: string): Promise<RecurringInvoice> {
  const { data } = await api.post(`/invoices/recurring/${id}/pause`);
  return data;
}

export async function resumeRecurringInvoice(id: string): Promise<RecurringInvoice> {
  const { data } = await api.post(`/invoices/recurring/${id}/resume`);
  return data;
}

export async function cancelRecurringInvoice(id: string): Promise<RecurringInvoice> {
  const { data } = await api.post(`/invoices/recurring/${id}/cancel`);
  return data;
}

export async function runDueRecurringInvoices(asOf?: string): Promise<RunDueResult> {
  const params: Record<string, string> = {};
  if (asOf) params.as_of = asOf;
  const { data } = await api.post("/invoices/recurring/run-due", null, { params });
  return data;
}
